import { BufferGeometry } from "three";
import { Renderer } from "./Renderer";
import { DISABLE_STREAMING_DRAW } from "../globals";
import { UsingMultiDraw, UsingDrawIndirect } from "./globals";
import { BYTES_PER_DRAWCALL } from "./gpudrivenrender/IndirectBufferBuilder";
import { BufferManagerWebGPU } from './BufferManagerWebGPU';
import { Megabytes } from "../globals";
import { MAX_BYTES_PER_SECOND, MAPPED_BUF_SIZE } from "../globals";
import { StagingBuffers } from "./StagingBuffers";
import { isSafari, isIOSDevice } from "../../compat.js";
/** @import { MultiDrawBatch } from "./gpudrivenrender/MultiDrawBatch" */
/** @import { RenderBatch } from "../scene/RenderBatch" */


/** @enum {number} IndirectDrawType */
const IndirectDrawType = {
  NONE: 0,
  INDIRECT: 1,
  MULTI_DRAW: 2
};

export class VertexBuffer {

  /** @type {GPUBuffer|undefined} */
  #curVB;
  /** @type {GPUBuffer|undefined} */
  #curIB;
  /** @type {"uint16" | "uint32"} */
  #curIBType;
  /** @type {StateTrackingEncoder|undefined} */
  #curPass;

  /** @type {Renderer} */
  #renderer;
  /** @type {BufferManagerWebGPU} */
  #uploadIndexBufferManager;
  /** @type {BufferManagerWebGPU} */
  #streamIndexBufferManager;
  /** @type {BufferManagerWebGPU} */
  #uploadVertexBufferManager;
  /** @type {BufferManagerWebGPU} */
  #streamVertexBufferManager;

  /** @type {StagingBuffers} */
  #staging;
  /** @type {Set.<BufferGeometry>} */
  #streamDraws = new Set();
  #cleanups = 0;
  #bytesWritten = 0;
  #overWriteLimit = false;
  #dataFlushes = 0;
  #numWrites = 0;
  #numDraws = 0;
  /** @type {number} */
  #maxBytesPerFrame; // set based on fps target

  /**
   * @type {boolean} Whether progressive rendering is enabled in the Viewer:
   *                   - If true, we limit the upload budget per frame to support progressive rendering.
   *                   - If false, we disable the limit to force full frames.
   **/
  #progressive = true;

  /** @type {IndirectDrawType} */
  #indirectDrawType = IndirectDrawType.NONE;

  /**
   * On WebKit (desktop Safari and all iOS browsers), bypass the shared StagingBuffers copy path
   * and upload directly via queue.writeBuffer(). WebKit fails queue.submit() when one command
   * buffer contains 2+ copyBufferToBuffer commands reading from the same host-mapped source buffer
   * (which is exactly how StagingBuffers batches writes), and splitting into one command buffer per
   * copy explodes the submit size (hundreds per frame) and loses the GPU context on larger models.
   * See #writeBuffer().
   * @type {boolean}
   */
  #bypassStaging = isSafari() || isIOSDevice();

  /**
   * @param {Renderer} renderer
   * @param {StagingBuffers} staging Shared staging buffers used for the per-frame upload path.
   */
  constructor(renderer, staging) {
    this.#renderer = renderer;
    const device = renderer.getDevice();

    const vertexBufferDesc = {
      label: 'vertex-buffer',
      size: Megabytes(64),
      usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST
    };

    const indexBufferDesc = {
      label: 'index-buffer',
      size: Megabytes(16),
      usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST
    };

    this.#uploadIndexBufferManager = new BufferManagerWebGPU(device, indexBufferDesc, { freesAreBatched: true });
    this.#uploadVertexBufferManager = new BufferManagerWebGPU(device, vertexBufferDesc, { freesAreBatched: true });

    this.#streamIndexBufferManager = new BufferManagerWebGPU(device, indexBufferDesc, { freesAreBatched: true });
    this.#streamVertexBufferManager = new BufferManagerWebGPU(device, vertexBufferDesc, { freesAreBatched: true });

    this.#staging = staging;
    this.setFpsTarget(30);

    if (UsingMultiDraw()) {
      this.#indirectDrawType = IndirectDrawType.MULTI_DRAW;
    } else if (UsingDrawIndirect()) {
      this.#indirectDrawType = IndirectDrawType.INDIRECT;
    }
  }

  static #disposeHandler(event) {
    const geometry = event.target;
    geometry.removeEventListener('dispose', VertexBuffer.#disposeHandler);
    VertexBuffer.deallocateGeometry(geometry);
  }

  /**
   * Free up vertex buffer allocatations for this geometry, if there are any.
   * @param {BufferGeometry} geometry
  */
  static deallocateGeometry(geometry) {
    // The event listener class overrides the this context when invoking this handler.
    // So we need to access the BufferManagers instance via geometry.__gpuVertexBufferManager and geometry.__gpuIndexBufferManager.
    if (geometry.__gpuvb) {
      const block = geometry.__gpuvbBlock;
      const bufferManager = geometry.__gpuVertexBufferManager;
      if (block) {
        bufferManager.free(block);
      }
      geometry.__gpuvb = undefined;
      geometry.__gpuvbBlock = undefined;
      geometry.__gpuvbSize = undefined;
      geometry.__gpuvbBaseVertex = undefined;
    }

    if (geometry.__gpuib) {
      const block = geometry.__gpuibBlock;
      const bufferManager = geometry.__gpuIndexBufferManager;
      if (block) {
        bufferManager.free(block);
      }
      geometry.__gpuib = undefined;
      geometry.__gpuibBlock = undefined;
      geometry.__gpuibSize = undefined;
      geometry.__gpuibBaseIndex = undefined;
      geometry.__gpuibLinesOffset = undefined;
      geometry.__gpuibType = undefined;
      geometry.__gpuibShift = undefined;
    }

    geometry.__gpuVertexBufferManager = undefined;
    geometry.__gpuIndexBufferManager = undefined;
  }

  /**
   * Flush all buffer copies from the staging buffers into the upload and stream
   * allocator buffers.
   * flushWrites() needs to be called before submitting any command buffers that
   * referenced any of VertexBuffer's GPUBuffers.
   */
  flushWrites() {
    if (this.#staging.flushData()) {
      this.#dataFlushes++;
    }
  }

  /**
   * @param {number} fpsTarget
   */
  setFpsTarget(fpsTarget) {
    this.maxBytesPerFrame = MAX_BYTES_PER_SECOND / fpsTarget; // default to 30fps
    this.#staging.maxBufsPerFrame = Math.ceil(this.maxBytesPerFrame / MAPPED_BUF_SIZE);
  }

  /**
   * When progressive rendering is disabled the per-frame upload budget must not be
   * enforced: a full render must be able to upload all geometry in a single frame.
   * @param {boolean} progressive
   */
  setProgressiveRendering(progressive) {
    this.#progressive = progressive;
  }

  /**
   * @param {BufferGeometry} geom Owner of the copy, tracked so updateBufferRange can detect a
   *   not-yet-flushed staged copy and defer the partial sync to a full re-upload.
   * @param {GPUBuffer} dstBuffer
   * @param {number} dstOffset
   * @param {ArrayBuffer} srcBuffer
   * @param {number} srcOffset
   * @param {number} size
   */
  #writeBuffer(geom, dstBuffer, dstOffset, srcBuffer, srcOffset, size) {
    this.#bytesWritten += size;
    this.#numWrites++;
    if (this.#bytesWritten > this.maxBytesPerFrame) {
      this.#overWriteLimit = true;
    }

    // On Safari we bypass staging entirely (see #bypassStaging). Otherwise use the staging copy
    // path and fall back to GPUQueue.writeBuffer() only when the staging buffers are full. The
    // fallback is not tracked: it is enqueued immediately, so it cannot race ahead of a pending copy.
    if (this.#bypassStaging || !this.#staging.writeData(dstBuffer, dstOffset, srcBuffer, srcOffset, size, geom)) {
      this.#renderer.getDevice().queue.writeBuffer(dstBuffer, dstOffset, srcBuffer, srcOffset, size);
    }
  }

  /**
   * Copy an index typed array into a fresh, zero-padded ArrayBuffer of `paddedByteLength` bytes.
   * Copies raw bytes (not elements) so it is correct for both Uint16Array and Uint32Array index
   * buffers. Used when the source ArrayBuffer has no spare bytes for the +2 padding we add to make
   * a triangle/line index copy length a multiple of 4.
   * @param {Uint16Array|Uint32Array} indices
   * @param {number} paddedByteLength
   * @returns {ArrayBuffer}
   */
  static #padIndexData(indices, paddedByteLength) {
    const padded = new Uint8Array(paddedByteLength);
    padded.set(new Uint8Array(indices.buffer, indices.byteOffset, indices.byteLength));
    return padded.buffer;
  }

  /**
   *
   * @param {BufferGeometry} geometry
   * @param {boolean} useStreamAllocator
   */
  #initVB(geometry, useStreamAllocator) {
    if ((geometry.__gpuvb || geometry.__gpuib) && !geometry.vbNeedsUpdate) {
      return;
    }

    // If we've initialized this VertexBuffer already we free up the GPU memory
    // we used previously.
    // @todo: We could instead detect if the previously used buffers are the same size
    // as what we want this time, and reuse them if so. This would save a small amount
    // processing time, and potentially reduce some fragmentation of the buffer.
    if (geometry.__gpuvb || geometry.__gpuib) {
      VertexBuffer.deallocateGeometry(geometry);
    }

    const vertexBufferManager = useStreamAllocator ? this.#streamVertexBufferManager : this.#uploadVertexBufferManager;
    const indexBufferManager = useStreamAllocator ? this.#streamIndexBufferManager : this.#uploadIndexBufferManager;

    geometry.__gpuVertexBufferManager = vertexBufferManager;
    geometry.__gpuIndexBufferManager = indexBufferManager;

    let strideBytes = geometry.vbstride * 4;
    const memoryBlock = vertexBufferManager.allocate(geometry.vb.byteLength, strideBytes);

    const vb = memoryBlock.buffer;
    const baseVertex = memoryBlock.offset / strideBytes;
    this.#writeBuffer(geometry, vb, baseVertex * strideBytes, geometry.vb.buffer, geometry.vb.byteOffset, geometry.vb.byteLength);

    geometry.__gpuvb = vb;
    geometry.__gpuvbBlock = memoryBlock;
    geometry.__gpuvbSize = geometry.vb.byteLength;
    geometry.__gpuvbBaseVertex = baseVertex;

    let len4 = geometry.ib.byteLength;
    if (len4 % 4) len4 += 2;

    let lenLines = geometry.iblines?.byteLength || 0;
    if (lenLines % 4) lenLines += 2;

    const ibSize = len4 + lenLines;
    const indexAlloc = indexBufferManager.allocate(ibSize);
    const ib = indexAlloc.buffer;
    const baseIndexOffset = indexAlloc.offset;

    if (geometry.ib.byteOffset + len4 > geometry.ib.buffer.byteLength) {
      // TODO: https://autodesk.atlassian.net/browse/VIZX-1666
      // This log is very noisy.
      // console.log("OUT OF BOUNDS FIX", ib.offset, len4);
      //This is an annoying case where the underlying array buffer doesn't have extra bytes to
      //compensate for the extra 2 bytes we add to the copy length to make it multiple of 4
      //Note that only triangle index buffers can be non-multiple of 4.
      this.#writeBuffer(geometry, ib, baseIndexOffset,
      VertexBuffer.#padIndexData(geometry.ib, len4), 0, len4);
    } else {
      this.#writeBuffer(geometry, ib, baseIndexOffset, geometry.ib.buffer, geometry.ib.byteOffset, len4);
    }

    if (geometry.iblines) {
      if (geometry.iblines.byteOffset + lenLines > geometry.iblines.buffer.byteLength) {
        // Same out-of-bounds case as the triangle index buffer above: the underlying
        // ArrayBuffer has no spare bytes for the +2 padding we add to make lenLines a
        // multiple of 4, so copy through a padded temp buffer to avoid reading past the end.
        this.#writeBuffer(geometry, ib, baseIndexOffset + len4,
        VertexBuffer.#padIndexData(geometry.iblines, lenLines), 0, lenLines);
      } else {
        this.#writeBuffer(geometry, ib, baseIndexOffset + len4,
        geometry.iblines.buffer, geometry.iblines.byteOffset, lenLines);
      }
    }

    geometry.__gpuib = ib;
    geometry.__gpuibBlock = indexAlloc;

    if (geometry.ib instanceof Uint16Array) {
      geometry.__gpuibType = "uint16";
      geometry.__gpuibShift = 1;
      geometry.iblines && (geometry.__gpuibLinesOffset = len4 / 2);
    } else if (geometry.ib instanceof Uint32Array) {
      geometry.__gpuibType = "uint32";
      geometry.__gpuibShift = 2;
      geometry.iblines && (geometry.__gpuibLinesOffset = len4 / 4);
    } else {
      console.warn("unknown index buffer type");
    }

    geometry.__gpuibBaseIndex = baseIndexOffset >> geometry.__gpuibShift;
    geometry.__gpuibSize = ibSize;

    geometry.vbNeedsUpdate = false;

    if (vertexBufferManager === this.#uploadVertexBufferManager) {
      geometry.addEventListener('dispose', VertexBuffer.#disposeHandler);
    }

    // This is useful to debug memory leaks.
    // console.log(geometry.id, vertexBufferManager.computeUsedBytes());
  }

  /**
   * @param {BufferGeometry} geom
  */
  initUploaded(geom) {
    if (this.#streamDraws.has(geom)) {
      VertexBuffer.deallocateGeometry(geom);
      this.#streamDraws.delete(geom);
    }

    this.#initVB(geom, false);
  }

  /**
   * Mark geometry for a full GPU re-upload on the next draw and invalidate affected render bundles.
   * @param {BufferGeometry} geom
   * @param {RenderBatch[]|undefined} renderBatches
   */
  #markGeometryDirty(geom, renderBatches) {
    if (geom.vbNeedsUpdate) {
      return;
    }
    geom.vbNeedsUpdate = true;
    this.#renderer.invalidateRenderBundlesForBatches(renderBatches);
  }

  /**
   * Update a range of vertices and/or indices in an already uploaded geometry.
   *
   * Note: calling this twice in the same frame may cause a full re-upload, since staged
   * copies cannot be updated after staging.
   *
   * @param {BufferGeometry} geom
   * @param {number} [vertexStart] - Start vertex index
   * @param {number} [vertexEnd] - End vertex index (exclusive)
   * @param {number} [indexStart] - Start index in index buffer
   * @param {number} [indexEnd] - End index in index buffer (exclusive)
   * @param {RenderBatch[]|undefined} [renderBatches] - Batches to invalidate on full re-upload fallback.
   */
  updateBufferRange(geom, vertexStart, vertexEnd, indexStart, indexEnd, renderBatches) {
    // For streaming geometries, just mark as needing update
    // The streaming system will re-allocate and upload from CPU memory on next frame
    if (geom.streamingDraw) {
      if (vertexStart !== undefined && vertexEnd !== undefined) {
        // No need to invalidate render bundles for streaming geometries
        // since we only generate those for uploaded geometries
        geom.vbNeedsUpdate = true;
      }
      return;
    }

    // Full re-upload already pending — #prepareDraw will upload on next draw.
    if (geom.vbNeedsUpdate) {
      return;
    }

    // Staged uploads are only flushed before draw. Defer partial GPU sync until the
    // geometry is allocated and this geometry's own pending staged copy has been flushed.
    if (!geom.__gpuvbBlock ||
    this.#staging.hasPendingCopy(geom)) {
      this.#markGeometryDirty(geom, renderBatches);
      return;
    }

    // Update vertex buffer if range specified
    if (vertexStart !== undefined && vertexEnd !== undefined) {
      // Geometry must already be uploaded
      if (!geom.vb) {
        console.error('Cannot update vertex buffer range: no CPU data');
        return;
      }

      const strideBytes = geom.vbstride * 4;
      const startOffset = (geom.__gpuvbBaseVertex + vertexStart) * strideBytes;
      const count = vertexEnd - vertexStart;
      const size = count * strideBytes;

      // Calculate source offset in the CPU buffer
      const srcOffset = geom.vb.byteOffset + vertexStart * geom.vbstride * 4;

      // Write the range to the GPU buffer
      this.#writeBuffer(
        geom,
        geom.__gpuvb,
        startOffset,
        geom.vb.buffer,
        srcOffset,
        size
      );
    }

    // Update index buffer if range specified
    if (indexStart !== undefined && indexEnd !== undefined) {
      if (!geom.ib) {
        console.error('Cannot update index buffer range: no CPU data');
        return;
      }

      const bytesPerIndex = geom.ib.BYTES_PER_ELEMENT;
      const count = indexEnd - indexStart;
      const dstOffset = (geom.__gpuibBaseIndex + indexStart) * bytesPerIndex;
      const srcOffset = geom.ib.byteOffset + indexStart * bytesPerIndex;
      const size = count * bytesPerIndex;

      // Write the range to the GPU buffer
      this.#writeBuffer(
        geom,
        geom.__gpuib,
        dstOffset,
        geom.ib.buffer,
        srcOffset,
        size
      );
    }
  }

  /**
   * @param {BufferGeometry} geom
  */
  initStream(geom) {
    if (this.#streamDraws.has(geom)) {
      return;
    }

    this.#initVB(geom, true);
    this.#streamDraws.add(geom);
  }

  /**
   * Free up all blocks in the stream allocator.
   */
  #clearStreamBuffers() {
    for (const geom of this.#streamDraws) {
      if (geom.__gpuVertexBufferManager === this.#streamVertexBufferManager) {
        VertexBuffer.deallocateGeometry(geom);
      }
    }
    this.#streamDraws.clear();
    this.#streamVertexBufferManager.flushFreeBlocks();
    this.#streamIndexBufferManager.flushFreeBlocks();
  }

  /**
   * Free up unneeded memory and per-frame state tracking.
   * Should be called at the end of each frame.
   */
  cleanup() {
    this.#uploadIndexBufferManager.flushFreeBlocks();
    this.#uploadVertexBufferManager.flushFreeBlocks();
    this.#clearStreamBuffers();

    this.#bytesWritten = 0;
    this.#overWriteLimit = false;
    this.#cleanups++;
    this.#dataFlushes = 0;
    this.#numWrites = 0;
    this.#numDraws = 0;
  }

  stats() {
    const totalMem = this.getTotalMemory() / 1024 / 1024 / 1024; // GB
    console.log(`Vertex Buffer Memory: ${totalMem} GB`);
    console.log(`Bytes written: ${this.#bytesWritten / 1024 / 1024} MB`);
    console.log(`Writes: ${this.#numWrites}`);
    console.log(`Flushed times: ${this.#dataFlushes}`);
    console.log(`Draws: ${this.#numDraws}`);
  }

  /**
   * Get the total amount of memory being used, not counting the overhead of unused
   * space in currently allocated buffers.
   * @returns {number}
   */
  getTotalMemory() {
    let total = 0;
    total += this.#uploadVertexBufferManager.getTotalSize();
    total += this.#uploadIndexBufferManager.getTotalSize();
    total += this.#streamVertexBufferManager.getTotalSize();
    total += this.#streamIndexBufferManager.getTotalSize();
    total += this.#staging.totalAllocated();
    return total;
  }

  /**
   * @param {Buffer} geometry
   * @returns {boolean}
   */
  canDraw(geometry) {
    const needsUpload = !geometry.__gpuvb || geometry.vbNeedsUpdate;
    if (!needsUpload) {
      return true;
    } else {
      // Note: The +4 accounts for potential padding of ib/iblines in #initVB.
      const needed = geometry.vb.byteLength + geometry.ib.byteLength + (
      geometry.iblines?.byteLength || 0) + 4;
      return this.canUpload(needed);
    }
  }

  /**
   * @param {number} size
   * @returns {boolean} Whether or not size bytes can be uploaded this frame.
   */
  canUpload(size) {
    if (!this.#progressive) {
      return true;
    }

    // Allow one geometry per frame to be uploaded if it's bigger than the max
    // allowed copied bytes per frame.
    if (size > this.maxBytesPerFrame && !this.#overWriteLimit) {
      return true;
    }
    const leftThisFrame = this.maxBytesPerFrame - this.#bytesWritten;
    return leftThisFrame > size;
  }

  /**
   * Make sure that vb and ib are set on the passEncoder
   * @param {StateTrackingEncoder} passEncoder
   * @param {GPUBuffer} vb
   * @param {GPUBuffer} ib
   * @param {"uint16" | "uint32"} ibType
   */
  #setCurrentBuffers(passEncoder, vb, ib, ibType) {

    // If passEncoder changed, set both buffers
    if (this.#curPass !== passEncoder) {
      passEncoder.setVertexBuffer(0, vb);
      this.#curVB = vb;

      passEncoder.setIndexBuffer(ib, ibType);
      this.#curIB = ib;
      this.#curIBType = ibType;

      this.#curPass = passEncoder;
    } else {
      // passEncoder is the same - only set the buffers if they changed
      if (vb !== this.#curVB) {
        passEncoder.setVertexBuffer(0, vb);
        this.#curVB = vb;
      }

      if (ib !== this.#curIB || ibType !== this.#curIBType) {
        passEncoder.setIndexBuffer(ib, ibType);
        this.#curIB = ib;
        this.#curIBType = ibType;
      }
    }
  }

  /**
   * @param {StateTrackingEncoder} passEncoder
   * @param {BufferGeometry} geometry
   */
  #prepareDraw(passEncoder, geometry) {
    const geometryNeedsUpdate = !geometry.__gpuvb || geometry.vbNeedsUpdate;

    if (geometryNeedsUpdate) {
      if (geometry.streamingDraw && !DISABLE_STREAMING_DRAW) {
        // Currently happening for unoptimized 3D model tiles and overlays.
        // For Tandem, we never use this.
        this.initStream(geometry);
      } else {
        // Currently happening for 2D geometry and tiles selected for optimization by OutOfCoreTileManager.
        // Done for all geometry on Tandem (DISABLE_STREAMING_DRAW).
        this.initUploaded(geometry);
      }
    }

    const vb = geometry.__gpuvb;
    const ib = geometry.__gpuib;
    if (!vb || !ib) {
      console.error('Still missing VB or IB when drawing!');
      return;
    }

    this.#numDraws++;
    this.#setCurrentBuffers(passEncoder, vb, ib, geometry.__gpuibType);
  }

  /**
   * @param {StateTrackingEncoder} passEncoder
   * @param {BufferGeometry} geometry
   * @param {number} instanceId
   */
  draw(passEncoder, geometry, instanceId) {
    this.#prepareDraw(passEncoder, geometry);

    const instanceCount = geometry.numInstances ?? 1;
    passEncoder.drawIndexed(geometry.ib.length, instanceCount, geometry.__gpuibBaseIndex, geometry.__gpuvbBaseVertex, instanceId);
  }

  /**
   * @param {StateTrackingEncoder} passEncoder
   * @param {BufferGeometry} geometry
   * @param {number} instanceId
   */
  drawEdges(passEncoder, geometry, instanceId) {
    if (!geometry.iblines) {
      return;
    }

    this.#prepareDraw(passEncoder, geometry);

    const instanceCount = geometry.numInstances ?? 1;
    let firstIndex = geometry.__gpuibBaseIndex + geometry.__gpuibLinesOffset;
    passEncoder.drawIndexed(geometry.iblines.length, instanceCount, firstIndex, geometry.__gpuvbBaseVertex, instanceId);
  }

  /** Run a multi-draw call for the given geometry.
   * Note: It's required that vb and ib are already uploaded.
   * @param {StateTrackingEncoder} passEncoder
   * @param {MultiDrawCall} multiDraw
   * @param {MultiDrawBatch} mdBatch
   * @param {number} multiDrawIndex - Index of this multiDraw within the batch. Selects the per-multiDraw
   *     visible draw-call count written by the cull shader (drawCallCountsBuffer[multiDrawIndex]).
   * @param {bool} [drawEdges=false] By Default, we render geometry. If true, we render edges instead.
   **/
  drawMulti(passEncoder, multiDraw, mdBatch, multiDrawIndex, drawEdges = false) {
    if (this.#indirectDrawType === IndirectDrawType.NONE) {
      console.error('VertexBuffer.drawMulti() was called but indirect draws are disabled.');
      return;
    }

    // Make sure vertex buffer and index buffer are set
    const vb = multiDraw.vb;
    const ib = multiDraw.ib;
    const ibType = multiDraw.firstGeometry.__gpuibType;
    this.#setCurrentBuffers(passEncoder, vb, ib, ibType);

    let drawCallsBlock = drawEdges ?
    mdBatch.edgeDrawCallsBlock :
    mdBatch.drawCallsBlock;

    if (this.#indirectDrawType === IndirectDrawType.MULTI_DRAW) {
      passEncoder.multiDrawIndexedIndirect(
        drawCallsBlock.buffer,
        drawCallsBlock.offset + multiDraw.indirectOffset,
        multiDraw.drawCount,
        mdBatch.drawCallCountsBuffer,
        multiDrawIndex * 4
      );

    } else if (this.#indirectDrawType === IndirectDrawType.INDIRECT) {
      for (let i = 0; i < multiDraw.drawCount; i++) {
        passEncoder.drawIndexedIndirect(
          drawCallsBlock.buffer,
          drawCallsBlock.offset + multiDraw.indirectOffset + BYTES_PER_DRAWCALL * i);
      }
    }
  }
}